import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { getLocales } from 'assets/locales/Locale';
import { normalizeCountryCode } from 'assets/utils/helpers/NormalizationHelper';
import ClaimJobItemPayee from 'models/claims/claimJobItemPayee/Model.ClaimJobItemPayee';
import ClaimJobStatusCode from 'models/enums/ClaimJobStatusCode';
import LineItem from 'models/enums/LineItem';
import { useMemo } from 'react';
import { useClaimEditJobPayeeItem, useClaimJobEdit } from '../ClaimEdit.Job.Hooks';
import Theme from 'assets/themes/Theme.Common';

type Props = {
  data?: Utils.FormData<Model.IClaimJobItem> & { isWarrantyProduct: boolean };
  onUpdate(data: Partial<Model.IClaimJobItem>): void;
  countryCode?: Model.Enum.CountryCode;
};

export default function ClaimEditJobItemDeductible({ data, countryCode, onUpdate }: Props) {
  const { lang } = getLocales();
  const {
    getJobItemDefaultValues,
    isClaimJobItemEditable,
    isClaimAdmin,
    permissions,
    contract,
    isClaimOwner,
    claimJob,
  } = useClaimJobEdit();
  const { payeeList, setOffset, setSearch, search, limit, offset } = useClaimEditJobPayeeItem();

  const filteredPayees = useMemo(
    () =>
      payeeList?.filter((it) => {
        const cc = it?.payee?.countryCode ?? it?.client?.countryCode ?? it?.dealer?.countryCode;
        return normalizeCountryCode(cc) === countryCode; // Filter based on countryCode
      }),
    [countryCode, payeeList]
  );

  // Use client side pagination for select input
  const searchedPayees = useMemo(() => {
    if (search) {
      setOffset(0);
      return filteredPayees.filter((p) => {
        const payee = p.client ?? p.dealer ?? p.payee;
        return payee?.displayInfo?.title?.toLowerCase()?.includes(search.toLowerCase());
      });
    } else return filteredPayees;
  }, [filteredPayees, search]);

  const paginatedPayees = useMemo(() => {
    const applyPagination = (list: ClaimJobItemPayee[], limit: number, offset: number) => {
      const start = offset * limit;
      const end = start + limit;
      return list.slice(start, end);
    };
    return applyPagination(searchedPayees, limit, offset);
  }, [searchedPayees, limit, offset]);

  const payee = new ClaimJobItemPayee(data?.claimJobItemPayee);
  const payeeInfo = payee?.client ?? payee?.dealer ?? payee?.payee;

  const payeeId =
    data?.claimJobItemPayee?.payee?.id ?? data?.claimJobItemPayee?.client?.id ?? data?.claimJobItemPayee?.dealer?.id;
  const selectInputValue = payeeId ? `${payeeId}-${data?.claimJobItemPayee?.payeeType?.id}` : undefined;

  const isEditable =
    isClaimJobItemEditable ||
    !(isClaimAdmin || (isClaimOwner && (data?.id ? permissions?.EDIT_CLAIMJOBITEM : permissions?.CREATE_CLAIMJOBITEM)));

  return (
    <tr>
      <td>
        {payeeInfo &&
        data?.id &&
        (claimJob?.statusCode === ClaimJobStatusCode.OPEN_APPROVED_WAITING_ON_INVOICE || isEditable) ? (
          <span title={payeeInfo.displayInfo?.title}>{payeeInfo.displayInfo?.title}</span>
        ) : (
          <SelectInput
            name="claimJobItemPayee"
            data={paginatedPayees?.map((it) => ({ ...it.displayInfo, id: `${it.displayInfo.id}-${it.payeeType?.id}` }))}
            displayText={payee?.displayInfo?.title}
            value={selectInputValue}
            limit={limit}
            offset={offset}
            count={searchedPayees?.length}
            animateOut={() => {
              setOffset(0);
            }}
            error={!data?.claimJobItemPayee && data.jobItemTypeCode === LineItem.deductible && lang.mustNotBeEmpty}
            onSearch={setSearch}
            onOffsetChange={({ value: offset }) => setOffset(offset)}
            disabled={isEditable}
            onChange={({ claimJobItemPayee: id }) => {
              const itemPayee = paginatedPayees?.find(
                (it) => `${it.payee?.id ?? it.dealer?.id ?? it.client?.id}-${it.payeeType?.id}` === id
              );
              onUpdate({
                claimJobItemPayee: itemPayee,
                ...getJobItemDefaultValues({ claimJobItemPayee: itemPayee }),
                correction: contract?.deductible ?? 0.0,
              });
              setOffset(0); // Reset offset
              setSearch(undefined); // Reset search
            }}
            style={{
              input: {
                fontSize: Theme.FontSize.S,
              },
            }}
          />
        )}
      </td>
      <td>
        <NumberInput
          name="correction"
          value={data?.correction}
          onChange={onUpdate}
          step={0.01}
          disabled={
            isClaimJobItemEditable ||
            (data.jobItemTypeCode === LineItem.deductible && data?.isWarrantyProduct) ||
            !(isClaimAdmin || (isClaimOwner && permissions?.EDIT_CLAIMJOBITEM))
          }
          style={{ input: { textAlign: 'right', width: 'auto' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
      <td>
        <NumberInput
          name="adjudication"
          value={data?.adjudication}
          onChange={onUpdate}
          step={0.01}
          disabled={isClaimJobItemEditable || !(isClaimAdmin || (isClaimOwner && permissions?.EDIT_CLAIMJOBITEM))}
          style={{ input: { textAlign: 'right', width: 'auto' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
      <td style={{ width: 'auto' }}>
        <NumberInput
          name="total"
          value={data?.adjudication}
          readOnly
          onChange={onUpdate}
          style={{ input: { textAlign: 'right', width: 'auto' } }}
          iconBefore={<Icon class="fas-dollar-sign" />}
        />
      </td>
    </tr>
  );
}
